import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Button from '@cloudscape-design/components/button';
import Table from '@cloudscape-design/components/table';
import SideNavigation from '@cloudscape-design/components/side-navigation';
import Grid from '@cloudscape-design/components/grid';
import Box from '@cloudscape-design/components/box';
import Flashbar, { FlashbarProps } from '@cloudscape-design/components/flashbar';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import ContentLayout from '@cloudscape-design/components/content-layout';
import CodeEditor, { CodeEditorProps } from '@cloudscape-design/components/code-editor';
import Multiselect, { MultiselectProps } from '@cloudscape-design/components/multiselect';
import DateRangePicker, { DateRangePickerProps } from '@cloudscape-design/components/date-range-picker';
import SegmentedControl from '@cloudscape-design/components/segmented-control';
import BarChart from '@cloudscape-design/components/bar-chart';
import Checkbox from '@cloudscape-design/components/checkbox';
import ace from 'ace-builds';
import 'ace-builds/src-noconflict/mode-json';
import 'ace-builds/src-noconflict/mode-sql';
import 'ace-builds/src-noconflict/theme-dawn';
import 'ace-builds/src-noconflict/theme-tomorrow_night_bright';
import { runEsql, generateEsql, QueryResult } from '../api/query';
import { listStores } from '../api/stores';
import { StreamExplorer } from './explorer/StreamExplorer';

const PREBUILT_QUERIES: { label: string; description: string; query: string }[] =
  (window as unknown as Record<string, unknown>).__EVENTSTORE_PREBUILT_QUERIES__ as typeof PREBUILT_QUERIES || [];

const HISTORY_KEY = 'eventstore-query-history';
const MAX_HISTORY = 10;

interface HistoryEntry {
  query: string;
  timestamp: string;
}

function loadHistory(): HistoryEntry[] {
  try {
    return JSON.parse(localStorage.getItem(HISTORY_KEY) || '[]');
  } catch { return []; }
}

function saveHistory(query: string) {
  const history = loadHistory();
  history.unshift({ query, timestamp: new Date().toISOString() });
  localStorage.setItem(HISTORY_KEY, JSON.stringify(history.slice(0, MAX_HISTORY)));
}

export function Explorer() {
  const navigate = useNavigate();
  // Two modes share this route:
  //   ?mode=query   ESQL workbench (default; existing behavior)
  //   ?mode=stream  Cross-store stream lifecycle browser (Stage 8)
  // The mode toggle preserves the rest of the URL state so users can bounce
  // between modes and "Query this" / stream cross-links work.
  const [searchParams, setSearchParams] = useSearchParams();
  const mode = searchParams.get('mode') === 'stream' ? 'stream' : 'query';
  const urlQuery = searchParams.get('q');
  const urlAutorun = searchParams.get('autorun') === 'true';

  const setMode = (next: 'query' | 'stream') => {
    const p = new URLSearchParams(searchParams);
    p.set('mode', next);
    setSearchParams(p, { replace: false });
  };

  const [queryText, setQueryText] = useState(
    urlQuery
      || (PREBUILT_QUERIES.length > 0 ? PREBUILT_QUERIES[0].query : 'SELECT * FROM "payments" LIMIT 50')
  );
  const [results, setResults] = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(false);
  const [flash, setFlash] = useState<FlashbarProps.MessageDefinition[]>([]);
  const [truncated, setTruncated] = useState(false);
  const [resultCount, setResultCount] = useState(0);
  const [preferences, setPreferences] = useState<CodeEditorProps.Preferences>({ theme: 'dawn', wrapLines: true });
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const editorRef = React.useRef<any>(null);

  // Sync editor theme with app theme
  useEffect(() => {
    const stored = localStorage.getItem('eventstore-theme');
    const dark = stored ? stored === 'dark' : window.matchMedia('(prefers-color-scheme: dark)').matches;
    setPreferences(p => ({ ...p, theme: dark ? 'tomorrow_night_bright' : 'dawn' }));
    const onStorage = () => {
      const d = localStorage.getItem('eventstore-theme') === 'dark';
      setPreferences(p => ({ ...p, theme: d ? 'tomorrow_night_bright' : 'dawn' }));
    };
    window.addEventListener('storage', onStorage);
    const interval = setInterval(onStorage, 1000);
    return () => { window.removeEventListener('storage', onStorage); clearInterval(interval); };
  }, []);
  const [storeOptions, setStoreOptions] = useState<MultiselectProps.Option[]>([]);
  const [selectedStores, setSelectedStores] = useState<MultiselectProps.Option[]>([]);
  const [dateRange, setDateRange] = useState<DateRangePickerProps.Value | null>(null);
  const [viewMode, setViewMode] = useState('table');
  const [history, setHistory] = useState<HistoryEntry[]>(loadHistory());
  const [showChart, setShowChart] = useState(false);

  // Fetch stores
  useEffect(() => {
    listStores({ limit: 100 }).then(res => {
      const stores = (res.data as { stores?: { name: string }[] })?.stores || [];
      setStoreOptions(stores.map(s => ({ label: s.name, value: s.name })));
    }).catch(() => {});
  }, []);

  // Sync multiselect from query text — extract store names from ESQL FROM clause
  useEffect(() => {
    const matches = queryText.match(/FROM\s+(?:stores?\(|")([\w'-]+)/gi);
    if (matches) {
      const stores = matches.map(m => {
        const nameMatch = m.match(/['"]([^'"]+)['"]/);
        return nameMatch?.[1] || m.replace(/.*FROM\s+["]/i, '').replace(/".*/, '');
      }).filter(Boolean);
      setSelectedStores(stores.map(s => ({ label: s, value: s })));
    }
  }, [queryText]);

  const updateQueryStores = useCallback((stores: string[]) => {
    // For ESQL, user edits the FROM clause directly
  }, []);

  const applyTimeRange = useCallback((range: DateRangePickerProps.Value | null) => {
    if (!range) return;
    const now = new Date();
    let sinceDate: string;
    if (range.type === 'relative') {
      const ms: Record<string, number> = { second: 1000, minute: 60000, hour: 3600000, day: 86400000, week: 604800000, month: 2592000000, year: 31536000000 };
      sinceDate = new Date(now.getTime() - range.amount * (ms[range.unit] || 0)).toISOString();
    } else {
      sinceDate = range.startDate;
    }
    // Remove existing SINCE clause and append new one
    const withoutSince = queryText.replace(/\s+SINCE\s+'[^']*'/i, '');
    setQueryText(`${withoutSince.trimEnd()} SINCE '${sinceDate}'`);
  }, [queryText]);

  const handleAiGenerate = async () => {
    setAiLoading(true);
    try {
      const storeNames = selectedStores.map(s => s.value || '');
      const resp = await generateEsql(aiPrompt, storeNames);
      if (resp?.esql) {
        setQueryText(resp.esql);
        setResults(resp.results || []);
        setResultCount(resp.count || 0);
        setTruncated(resp.truncated || false);
        setAiPrompt('');
      }
    } catch (e: any) {
      setFlash([{ type: 'error', content: `AI generation failed: ${e.message}`, dismissible: true, onDismiss: () => setFlash([]) }]);
    }
    setAiLoading(false);
  };

  // When Stream mode's "Query this" navigates here with ?q=…&autorun=true,
  // sync the editor state to the URL and kick off execution once. We strip
  // autorun from the URL after firing so a refresh doesn't re-execute.
  useEffect(() => {
    if (mode !== 'query' || !urlQuery) return;
    setQueryText(urlQuery);
    if (urlAutorun) {
      const p = new URLSearchParams(searchParams);
      p.delete('autorun');
      setSearchParams(p, { replace: true });
      // Defer to a microtask so setQueryText has committed before we read it
      setTimeout(() => { executeQueryRef.current?.(); }, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlQuery, urlAutorun, mode]);

  // Ref indirection so the useEffect above can reference executeQuery without
  // creating a mutual-dependency loop.
  const executeQueryRef = React.useRef<() => Promise<void>>();

  const executeQuery = async () => {
    setLoading(true);
    setFlash([]);
    // Clear previous error annotations
    if (editorRef.current) editorRef.current.session?.clearAnnotations();
    try {
      const response = await runEsql(queryText);
      setResults(response.results);
      setResultCount(response.count);
      setTruncated(response.truncated);
      saveHistory(queryText);
      setHistory(loadHistory());
    } catch (err: unknown) {
      const message = (err as Error).message || 'Query execution failed';
      setFlash([{ type: 'error', content: message, dismissible: true, onDismiss: () => setFlash([]) }]);
      // Highlight error position in editor
      const posMatch = message.match(/at character (\d+)/);
      if (posMatch && editorRef.current) {
        const charPos = parseInt(posMatch[1], 10);
        // Convert character offset to row/col
        let row = 0, col = charPos;
        for (const line of queryText.split('\n')) {
          if (col <= line.length) break;
          col -= line.length + 1;
          row++;
        }
        editorRef.current.session?.setAnnotations([{ row, column: col, text: message, type: 'error' }]);
      }
    } finally {
      setLoading(false);
    }
  };
  executeQueryRef.current = executeQuery;

  const loadMore = async () => {
    if (!results.length || !truncated) return;
    setLoading(true);
    try {
      const offset = results.length;
      const hasLimit = /LIMIT\s+\d+/i.test(queryText);
      const paginated = hasLimit
        ? queryText.replace(/LIMIT\s+\d+(\s+OFFSET\s+\d+)?/i, `LIMIT 50 OFFSET ${offset}`)
        : `${queryText} LIMIT 50 OFFSET ${offset}`;
      const response = await runEsql(paginated);
      setResults(prev => [...prev, ...response.results]);
      setResultCount(prev => prev + response.count);
      setTruncated(response.truncated);
    } catch (err: unknown) {
      const message = (err as Error).message || 'Failed to load more results';
      setFlash([{ type: 'error', content: message, dismissible: true, onDismiss: () => setFlash([]) }]);
    } finally {
      setLoading(false);
    }
  };

  const exportJSON = () => {
    const blob = new Blob([JSON.stringify(results, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'eventstore-query-results.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportCSV = () => {
    if (results.length === 0) return;
    const headers = Object.keys(results[0]);
    const csvRows = [
      headers.join(','),
      ...results.map(row =>
        headers.map(h => {
          const val = row[h];
          const str = val == null ? '' : typeof val === 'object' ? JSON.stringify(val) : String(val);
          return str.includes(',') || str.includes('"') || str.includes('\n')
            ? `"${str.replace(/"/g, '""')}"` : str;
        }).join(',')
      ),
    ];
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'eventstore-query-results.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const loadPrebuilt = (idx: number) => {
    setQueryText(PREBUILT_QUERIES[idx].query);
  };

  const columns = results.length > 0
    ? Object.keys(flattenForDisplay(results[0])).slice(0, 8).map(key => ({
        id: key,
        header: key,
        cell: (item: Record<string, unknown>) => {
          const val = item[key];
          return typeof val === 'object' ? JSON.stringify(val) : String(val ?? '');
        },
      }))
    : [];

  // Cross-link: from Stream mode's "Query this" button we switch to Query
  // mode with a pre-filled query and autorun. Passing setSearchParams keeps
  // the URL as the single source of truth.
  const handleQueryThis = (esql: string) => {
    const p = new URLSearchParams(searchParams);
    p.set('mode', 'query');
    p.set('q', esql);
    p.set('autorun', 'true');
    // clear stream-mode params so they don't linger
    p.delete('focus');
    setSearchParams(p, { replace: false });
  };

  return (
    <ContentLayout
      header={
        <Header
          variant="h1"
          description={mode === 'stream'
            ? 'Trace a stream key across every store'
            : 'Cross-store ESQL query workbench'}
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <SegmentedControl
                selectedId={mode}
                onChange={({ detail }) => setMode(detail.selectedId as 'query' | 'stream')}
                options={[
                  { id: 'query', text: 'Query' },
                  { id: 'stream', text: 'Stream' },
                ]}
              />
              {mode === 'query' && (
                <Button variant="primary" onClick={executeQuery} loading={loading}>
                  Run Query
                </Button>
              )}
            </SpaceBetween>
          }
        >
          Explorer
        </Header>
      }
    >
      {mode === 'stream' ? (
        <StreamExplorer onQueryThis={handleQueryThis} />
      ) : (
      <SpaceBetween size="l">
        <Flashbar items={flash} />

        <Grid gridDefinition={[{ colspan: 3 }, { colspan: 9 }]}>
          {/* Sidebar */}
          <SpaceBetween size="l">
            <Container header={<Header variant="h3">Pre-built Queries</Header>}>
              <SpaceBetween size="xs">
                {PREBUILT_QUERIES.map((q, idx) => (
                  <Box key={idx}>
                    <Button variant="link" onClick={() => loadPrebuilt(idx)}>
                      {q.label}
                    </Button>
                    <Box variant="small" color="text-body-secondary">{q.description}</Box>
                  </Box>
                ))}
              </SpaceBetween>
            </Container>

            <Container header={<Header variant="h3">Query History</Header>}>
              <SpaceBetween size="xs">
                {history.length === 0 && (
                  <Box variant="small" color="text-body-secondary">No history yet</Box>
                )}
                {history.map((entry, idx) => (
                  <Box key={idx}>
                    <Button variant="link" onClick={() => setQueryText(entry.query)}>
                      {new Date(entry.timestamp).toLocaleString()}
                    </Button>
                    <Box variant="small" color="text-body-secondary">
                      {entry.query.slice(0, 40)}...
                    </Box>
                  </Box>
                ))}
              </SpaceBetween>
            </Container>
          </SpaceBetween>

          {/* Main area */}
          <SpaceBetween size="l">
            <Container header={<Header variant="h3">Query</Header>}>
              <SpaceBetween size="m">
                <Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
                  <Multiselect
                    selectedOptions={selectedStores}
                    onChange={({ detail }) => {
                      setSelectedStores(detail.selectedOptions as MultiselectProps.Option[]);
                      updateQueryStores(detail.selectedOptions.map(o => o.value!));
                    }}
                    options={storeOptions}
                    placeholder="Select stores"
                    filteringType="auto"
                    tokenLimit={3}
                  />
                  <DateRangePicker
                    value={dateRange}
                    onChange={({ detail }) => {
                      setDateRange(detail.value);
                      applyTimeRange(detail.value);
                    }}
                    relativeOptions={[
                      { key: '1h', amount: 1, unit: 'hour', type: 'relative' },
                      { key: '6h', amount: 6, unit: 'hour', type: 'relative' },
                      { key: '24h', amount: 24, unit: 'hour', type: 'relative' },
                      { key: '7d', amount: 7, unit: 'day', type: 'relative' },
                    ]}
                    isValidRange={range => {
                      if (range?.type === 'absolute' && (!range.startDate || !range.endDate)) {
                        return { valid: false, errorMessage: 'Select start and end dates' };
                      }
                      return { valid: true };
                    }}
                    i18nStrings={{
                      todayAriaLabel: 'Today',
                      nextMonthAriaLabel: 'Next month',
                      previousMonthAriaLabel: 'Previous month',
                      customRelativeRangeDurationLabel: 'Duration',
                      customRelativeRangeDurationPlaceholder: 'Enter duration',
                      customRelativeRangeOptionLabel: 'Custom range',
                      customRelativeRangeOptionDescription: 'Set a custom time range',
                      customRelativeRangeUnitLabel: 'Unit',
                      formatRelativeRange: (range) => `Last ${range.amount} ${range.unit}${range.amount > 1 ? 's' : ''}`,
                      formatUnit: (unit, value) => `${unit}${value > 1 ? 's' : ''}`,
                      relativeModeTitle: 'Relative',
                      absoluteModeTitle: 'Absolute',
                      relativeRangeSelectionHeading: 'Choose a range',
                      startDateLabel: 'Start date',
                      startTimeLabel: 'Start time',
                      endDateLabel: 'End date',
                      endTimeLabel: 'End time',
                      dateTimeConstraintText: '',
                      clearButtonLabel: 'Clear',
                      cancelButtonLabel: 'Cancel',
                      applyButtonLabel: 'Apply',
                    }}
                    placeholder="Time range"
                  />
                </Grid>
                <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                  <input
                    type="text"
                    value={aiPrompt}
                    onChange={(e) => setAiPrompt(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && aiPrompt.trim() && selectedStores.length > 0) handleAiGenerate(); }}
                    placeholder="Describe what you want to find... (AI generates query)"
                    style={{ flex: 1, padding: '8px 12px', borderRadius: 8, border: '1px solid var(--color-border-input-default)', background: 'var(--color-background-input-default)', color: 'var(--color-text-body-default)', fontSize: 13 }}
                  />
                  <Button onClick={handleAiGenerate} loading={aiLoading} disabled={!aiPrompt.trim() || selectedStores.length === 0}>
                    ✨ Generate
                  </Button>
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                  {['Total settlements by merchant', 'High risk transactions over score 80', 'Declined payments this week', 'Event counts by type across all stores', 'Chargebacks over $500'].map(p => (
                    <button key={p} onClick={() => setAiPrompt(p)} style={{ padding: '4px 10px', borderRadius: 12, border: '1px solid var(--color-border-input-default)', background: 'transparent', color: 'var(--color-text-body-secondary)', fontSize: 11, cursor: 'pointer' }}>{p}</button>
                  ))}
                </div>
                <div id="esql-editor" ref={(el) => {
                  if (el && !editorRef.current) {
                    // Grab ace editor instance once mounted
                    setTimeout(() => {
                      const editorEl = el.querySelector('.ace_editor') as HTMLElement;
                      if (editorEl && (editorEl as any).env?.editor) editorRef.current = (editorEl as any).env.editor;
                    }, 500);
                  }
                }}>
                <CodeEditor
                  ace={ace}
                  language="sql"
                  value={queryText}
                  onChange={({ detail }) => { setQueryText(detail.value); if (editorRef.current) editorRef.current.session?.clearAnnotations(); }}
                  preferences={preferences}
                  onPreferencesChange={({ detail }) => setPreferences(detail)}
                  loading={false}
                  themes={{ light: ['dawn'], dark: ['tomorrow_night_bright'] }}
                  editorContentHeight={200}
                  i18nStrings={{
                    loadingState: 'Loading editor...',
                    errorState: 'Error loading editor',
                    errorStateRecovery: 'Retry',
                    editorGroupAriaLabel: 'Code editor',
                    statusBarGroupAriaLabel: 'Status bar',
                    cursorPosition: (row, column) => `Ln ${row}, Col ${column}`,
                    errorsTab: 'Errors',
                    warningsTab: 'Warnings',
                    preferencesButtonAriaLabel: 'Preferences',
                    paneCloseButtonAriaLabel: 'Close',
                    preferencesModalHeader: 'Preferences',
                    preferencesModalCancel: 'Cancel',
                    preferencesModalConfirm: 'Confirm',
                    preferencesModalWrapLines: 'Wrap lines',
                    preferencesModalTheme: 'Theme',
                    preferencesModalLightThemes: 'Light themes',
                    preferencesModalDarkThemes: 'Dark themes',
                  }}
                />
                </div>
              </SpaceBetween>
            </Container>

            <Container
              header={
                <Header
                  counter={`(${resultCount}${truncated ? '+' : ''})`}
                  description={truncated ? 'Results truncated — refine your query' : undefined}
                  actions={
                    <SpaceBetween direction="horizontal" size="xs">
                      <Button disabled={results.length === 0} onClick={exportJSON}>Export JSON</Button>
                      <Button disabled={results.length === 0} onClick={exportCSV}>Export CSV</Button>
                      <SegmentedControl
                        selectedId={viewMode}
                        onChange={({ detail }) => setViewMode(detail.selectedId)}
                        options={[
                          { id: 'table', text: 'Table' },
                          { id: 'json', text: 'JSON' },
                        ]}
                      />
                    </SpaceBetween>
                  }
                >
                  Results
                </Header>
              }
            >
              {viewMode === 'table' ? (
                <Table
                  items={results.map((r, i) => ({ ...flattenForDisplay(r), _idx: i, _raw: r }))}
                  columnDefinitions={[
                    ...columns,
                    {
                      id: 'stream',
                      header: 'Stream',
                      cell: (item: Record<string, unknown>) => {
                        // Extract a stream key from either an explicit `stream`
                        // column, a top-level pk (data-plane shape:
                        // tenant#store#stream), or nested data.paymentId etc.
                        const raw = item._raw as Record<string, unknown>;
                        const direct = raw.stream || item.stream;
                        const pk = raw.pk || item.pk;
                        let streamValue = typeof direct === 'string' ? direct : '';
                        if (!streamValue && typeof pk === 'string') {
                          const parts = pk.split('#');
                          if (parts.length >= 3) streamValue = parts.slice(2).join('#');
                        }
                        if (!streamValue) {
                          const d = raw.data as Record<string, unknown> | undefined;
                          const pid = d?.paymentId;
                          if (typeof pid === 'string') streamValue = pid;
                        }
                        if (!streamValue) return null;
                        const href = `/explorer?mode=stream&stream=${encodeURIComponent(streamValue)}`;
                        return (
                          <a
                            href={href}
                            onClick={(e) => {
                              e.preventDefault();
                              const p = new URLSearchParams(searchParams);
                              p.set('mode', 'stream');
                              p.set('stream', streamValue);
                              p.delete('focus');
                              setSearchParams(p, { replace: false });
                            }}
                          >
                            <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{streamValue}</span>
                          </a>
                        );
                      },
                    },
                    {
                      id: 'actions',
                      header: 'Actions',
                      cell: (item: Record<string, unknown>) => {
                        const raw = item._raw as Record<string, unknown>;
                        const metadata = raw.metadata as Record<string, unknown> | undefined;
                        const eventId = metadata?.eventId as string | undefined;
                        const storeName = (raw._storeName || '') as string;
                        if (!eventId || !storeName) return null;
                        const href = `/stores/${storeName}/lineage/${eventId}`;
                        return (
                          <a
                            href={href}
                            onClick={(e) => { e.preventDefault(); navigate(href); }}
                          >
                            Trace
                          </a>
                        );
                      },
                    },
                  ]}
                  empty={
                    <Box textAlign="center" color="inherit">
                      <StatusIndicator type="info">Run a query to see results</StatusIndicator>
                    </Box>
                  }
                  loading={loading}
                  loadingText="Querying..."
                  variant="embedded"
                  stickyHeader
                />
              ) : (
                <CodeEditor
                  ace={ace}
                  language="json"
                  value={JSON.stringify(results, null, 2)}
                  onChange={() => {}}
                  preferences={preferences}
                  onPreferencesChange={({ detail }) => setPreferences(detail)}
                  loading={false}
                  themes={{ light: ['dawn'], dark: ['tomorrow_night_bright'] }}
                  editorContentHeight={400}
                  i18nStrings={{
                    loadingState: 'Loading editor...',
                    errorState: 'Error loading editor',
                    errorStateRecovery: 'Retry',
                    editorGroupAriaLabel: 'Results JSON',
                    statusBarGroupAriaLabel: 'Status bar',
                    cursorPosition: (row, column) => `Ln ${row}, Col ${column}`,
                    errorsTab: 'Errors',
                    warningsTab: 'Warnings',
                    preferencesButtonAriaLabel: 'Preferences',
                    paneCloseButtonAriaLabel: 'Close',
                    preferencesModalHeader: 'Preferences',
                    preferencesModalCancel: 'Cancel',
                    preferencesModalConfirm: 'Confirm',
                    preferencesModalWrapLines: 'Wrap lines',
                    preferencesModalTheme: 'Theme',
                    preferencesModalLightThemes: 'Light themes',
                    preferencesModalDarkThemes: 'Dark themes',
                  }}
                />
              )}
            </Container>
            {truncated && (
              <Box textAlign="center">
                <Button onClick={loadMore} loading={loading}>Load More</Button>
              </Box>
            )}

            {results.length > 0 && hasAggregationData(results, queryText) && (
              <Container header={<Header variant="h3">Aggregation Chart</Header>}>
                <SpaceBetween size="s">
                  <Checkbox checked={showChart} onChange={({ detail }) => setShowChart(detail.checked)}>
                    Show chart
                  </Checkbox>
                  {showChart && <AggregationBarChart results={results} queryText={queryText} />}
                </SpaceBetween>
              </Container>
            )}
          </SpaceBetween>
        </Grid>
      </SpaceBetween>
      )}
    </ContentLayout>
  );
}

/** Flatten nested objects for table display (one level of data/metadata). */
function flattenForDisplay(obj: Record<string, unknown>): Record<string, unknown> {
  const flat: Record<string, unknown> = {};
  for (const [key, val] of Object.entries(obj)) {
    if (key.startsWith('_')) continue;
    if (key === 'data' && typeof val === 'object' && val !== null) {
      for (const [dk, dv] of Object.entries(val as Record<string, unknown>)) {
        flat[`data.${dk}`] = dv;
      }
    } else if (key === 'metadata' && typeof val === 'object' && val !== null) {
      for (const [mk, mv] of Object.entries(val as Record<string, unknown>)) {
        flat[`metadata.${mk}`] = mv;
      }
    } else {
      flat[key] = val;
    }
  }
  return flat;
}

function hasAggregationData(results: Record<string, unknown>[], queryText: string): boolean {
  if (results.length === 0) return false;
  if (!/group\s+by/i.test(queryText)) return false;
  const first = results[0];
  return Object.values(first).some(v => typeof v === 'number');
}

function getGroupByField(queryText: string): string | undefined {
  const match = queryText.match(/group\s+by\s+([\w.]+)/i);
  return match?.[1];
}

function AggregationBarChart({ results, queryText }: { results: Record<string, unknown>[]; queryText: string }) {
  const groupByField = getGroupByField(queryText);
  if (!groupByField) return null;

  // Find first numeric field for Y-axis
  const numericField = Object.keys(results[0]).find(k =>
    k === '_count' || typeof results[0][k] === 'number'
  ) || '_count';

  const series = [{
    title: numericField,
    type: 'bar' as const,
    data: results.map(r => ({
      x: String(r[groupByField] ?? ''),
      y: Number(r[numericField] ?? 0),
    })),
  }];

  return (
    <BarChart
      series={series}
      xTitle={groupByField}
      yTitle={numericField}
      height={250}
      hideFilter
      hideLegend
    />
  );
}
